import { useCallback, useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  changePassword,
  me,
  unlinkIdentity,
  linkGoogleIdentity,
  linkAppleIdentity,
  type AuthApiError,
  type MeResponse,
} from "@/app/api/auth-client";
import { getAuthConfig } from "@/config/auth";
import { GoogleSSOModal } from "@/app/components/google-sso-modal";
import { AppleSSOModal, type AppleSignInResult } from "@/app/components/apple-sso-modal";
import { BottomNav } from "./bottom-nav";
import { PasswordChangeSection, SSOManagementSection } from "./security";

interface PasswordSecurityPageProps {
  onBack: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

function identityErrorMessage(e: unknown): string {
  const err = e as Error & AuthApiError & { status?: number };
  if (err.error === "last_login_method" && err.message) return err.message;
  if (err.error === "email_mismatch" && err.message) return err.message;
  if (err.error === "not_linked" && err.message) return err.message;
  if (err.error === "already_linked" && err.message) return err.message;
  if (err.error === "google_in_use" && err.message) return err.message;
  if (err.error === "apple_in_use" && err.message) return err.message;
  return err.message || "Não foi possível concluir a operação.";
}

export function PasswordSecurityPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: PasswordSecurityPageProps) {
  const [account, setAccount] = useState<MeResponse | null>(null);
  const [accountLoad, setAccountLoad] = useState<"loading" | "error" | "ok">("loading");

  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [appleModalOpen, setAppleModalOpen] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);

  const { googleClientId, appleClientId, appleRedirectUri } = getAuthConfig();

  const refreshAccount = useCallback(async () => {
    try {
      const m = await me();
      setAccount(m);
      setAccountLoad("ok");
    } catch {
      setAccountLoad("error");
      setAccount(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await me();
        if (cancelled) return;
        setAccount(m);
        setAccountLoad("ok");
      } catch {
        if (cancelled) return;
        setAccountLoad("error");
        setAccount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasPasswordAccount = account ? account.has_password !== false : accountLoad !== "ok";

  const isGoogleConnected = accountLoad === "ok" && account?.google_linked === true;
  const isAppleConnected = accountLoad === "ok" && account?.apple_linked === true;
  const accountEmail = account?.email?.trim() ?? "";

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Senha alterada com sucesso.");
    } catch (e) {
      const err = e as Error & AuthApiError & { status?: number };
      const fromFields =
        err.fields?.new_password ||
        err.fields?.current_password ||
        (err.fields && Object.values(err.fields)[0]);
      const msg =
        err.message ||
        (typeof fromFields === "string" ? fromFields : undefined) ||
        (err.error === "invalid_credentials" || err.error === "invalid_current_password"
          ? "Senha atual incorreta."
          : undefined) ||
        (err.error === "no_password_set"
          ? "Esta conta não possui senha. Use recuperação de senha ou login social."
          : undefined) ||
        "Não foi possível alterar a senha.";
      toast.error(msg);
      throw e;
    }
  };

  const handleToggleGoogle = () => {
    if (isGoogleConnected) {
      if (
        !confirm(
          "Tem certeza que deseja desconectar sua conta Google? Você precisará de outro método de login (senha ou Apple)."
        )
      ) {
        return;
      }
      void (async () => {
        setGoogleBusy(true);
        try {
          await unlinkIdentity("google");
          toast.success("Conta Google desconectada.");
          await refreshAccount();
        } catch (e) {
          toast.error(identityErrorMessage(e));
        } finally {
          setGoogleBusy(false);
        }
      })();
    } else {
      if (!googleClientId) {
        toast.error("Login com Google não está configurado neste ambiente.");
        return;
      }
      setGoogleModalOpen(true);
    }
  };

  const handleGoogleIdToken = (idToken: string) => {
    setGoogleModalOpen(false);
    void (async () => {
      setGoogleBusy(true);
      try {
        await linkGoogleIdentity(idToken);
        toast.success("Google conectado à sua conta.");
        await refreshAccount();
      } catch (e) {
        toast.error(identityErrorMessage(e));
      } finally {
        setGoogleBusy(false);
      }
    })();
  };

  const handleToggleApple = () => {
    if (isAppleConnected) {
      if (
        !confirm(
          "Tem certeza que deseja desconectar sua conta Apple? Você precisará de outro método de login (senha ou Google)."
        )
      ) {
        return;
      }
      void (async () => {
        setAppleBusy(true);
        try {
          await unlinkIdentity("apple");
          toast.success("Conta Apple desconectada.");
          await refreshAccount();
        } catch (e) {
          toast.error(identityErrorMessage(e));
        } finally {
          setAppleBusy(false);
        }
      })();
    } else {
      if (!appleClientId) {
        toast.error("Sign in with Apple não está configurado neste ambiente.");
        return;
      }
      setAppleModalOpen(true);
    }
  };

  const handleAppleResult = (result: AppleSignInResult) => {
    setAppleModalOpen(false);
    void (async () => {
      setAppleBusy(true);
      try {
        await linkAppleIdentity(result.idToken, result.email, result.name);
        toast.success("Apple conectada à sua conta.");
        await refreshAccount();
      } catch (e) {
        toast.error(identityErrorMessage(e));
      } finally {
        setAppleBusy(false);
      }
    })();
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Senha e Segurança</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {accountLoad === "loading" ? null : hasPasswordAccount ? (
          <PasswordChangeSection onChangePassword={handleChangePassword} />
        ) : null}

        {accountLoad === "loading" ? (
          <div className="bg-card rounded-xl border border-border p-6 text-sm text-muted-foreground">
            Carregando métodos de autenticação…
          </div>
        ) : (
          <SSOManagementSection
            isGoogleConnected={isGoogleConnected}
            isAppleConnected={isAppleConnected}
            googleEmail={isGoogleConnected ? accountEmail : undefined}
            appleEmail={isAppleConnected ? accountEmail : undefined}
            onToggleGoogle={handleToggleGoogle}
            onToggleApple={handleToggleApple}
            googleBusy={googleBusy}
            appleBusy={appleBusy}
          />
        )}

        {/* 2FA: hidden until Phase B — docs/password-security-implementation-plan.md §5 */}
        {/* Active sessions: hidden until Phase B (server sessions API) — same doc §6 */}
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />

      <GoogleSSOModal
        isOpen={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
        onSelectAccount={handleGoogleIdToken}
        googleClientId={googleClientId}
      />
      <AppleSSOModal
        isOpen={appleModalOpen}
        onClose={() => setAppleModalOpen(false)}
        onSelectAccount={handleAppleResult}
        appleClientId={appleClientId}
        appleRedirectUri={appleRedirectUri}
      />
    </div>
  );
}
